import { useMemo, useState } from "react";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { resolveErrorContent, useTranslation } from "@i18n";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@Services/axiosWrapper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "@Services/constants";
import type { CreateViewResponse, UpdateViewResponse, LayoutData } from "@ServerTypes/layoutView/layoutView.schema";
import DragListComponent from "@CubeComponents/html/dragList";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import { LAYOUT_ERROR_CODE, PROJECT_DASHBOARD_RESOURCE_TYPE } from "@Helpers/constants";
import Text from "@CubeComponents/html/Text/Text";
import { getLayoutModuleIds } from "@Features/projects/utils/layoutModules";
import { layoutKind } from "@Features/projects/utils/layoutKind";
import {
  LAYOUT_NAME_ERROR,
  LAYOUT_NAME_ERROR_TEXT,
  validateLayoutName,
  type LayoutNameError,
} from "@Features/projects/utils/layoutNameValidation";

export interface SidebarIcon {
  id: string;
  name: string;
  svg: string;
  allow?: boolean;
}

// A type alias (not an interface) so it satisfies the DragListComponent's
// `T extends Record<string, unknown>` constraint — interfaces lack an implicit index signature.
type ModuleItem = {
  text: string;
  value: string;
  svg: string;
};

interface ConfigureLayoutModalProps {
  /** "mid" position sidebar icons used as the pool of available modules. */
  midIcons: SidebarIcon[];
  /** Whether a newly created layout is shared (chosen from the Create menu). Ignored when editing. */
  isShared: boolean;
  /** The full layout list, for per-namespace name-uniqueness checks. */
  layouts: LayoutData[];
  /** When set, the modal opens in "Configure" (edit) mode pre-filled with this layout's data. */
  layoutToEdit?: LayoutData;
  onClose: () => void;
  /** Called with the saved layout record (and whether it was an edit) so the caller can update its list. */
  onSaved: (layout: LayoutData, isEdit: boolean) => void;
}

function ConfigureLayoutModal({
  midIcons,
  isShared,
  layouts,
  layoutToEdit,
  onClose,
  onSaved,
}: ConfigureLayoutModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isEdit = layoutToEdit !== undefined;
  // Editing never changes a layout's namespace, so validate an edit against its own kind and a
  // create against the kind chosen from the Create menu.
  const targetIsShared = layoutToEdit ? layoutKind(layoutToEdit) === "shared" : isShared;

  // Full pool of selectable modules, keyed by module id for quick lookup.
  const modulePool = useMemo<ModuleItem[]>(
    () => midIcons.map((icon) => ({ text: icon.name, value: icon.id, svg: icon.svg })),
    [midIcons],
  );

  // In edit mode, seed "Added" from the layout's saved modules (in order); "Available" is the rest.
  // IMPORTANT: never drop a saved module id. Resolve each id against the FULL icon set (not just
  // enabled ones) so a temporarily-disabled module keeps its real label/icon, and fall back to the
  // raw id for genuinely-unknown ones. Otherwise saving a layout while a module is disabled would
  // permanently remove it from the layout (handleSave persists `added.map(v => v.value)`), even
  // after the module is re-enabled.
  const { initialAvailable, initialAdded } = useMemo(() => {
    const savedIds = getLayoutModuleIds(layoutToEdit);
    const iconById = new Map(midIcons.map((icon) => [icon.id, icon]));
    const added: ModuleItem[] = savedIds.map((id) => {
      const icon = iconById.get(id);
      return icon ? { text: icon.name, value: icon.id, svg: icon.svg } : { text: id, value: id, svg: "" };
    });
    const addedIds = new Set(added.map((module) => module.value));
    const available = modulePool.filter((module) => !addedIds.has(module.value));
    return { initialAvailable: available, initialAdded: added };
  }, [midIcons, modulePool, layoutToEdit]);

  const [name, setName] = useState<string>(layoutToEdit?.name ?? "");
  const [available, setAvailable] = useState<ModuleItem[]>(initialAvailable);
  const [added, setAdded] = useState<ModuleItem[]>(initialAdded);
  const [isLayoutSaving, setIsLayoutSaving] = useState(false);
  // A rejection the SERVER reported, kept separate from the client check because the server sees
  // other tabs' and other users' writes. Cleared as soon as the name is edited.
  const [serverNameError, setServerNameError] = useState<LayoutNameError | null>(null);
  // "Create" opens with an empty name, which is invalid but not yet the user's mistake, so the
  // required-name message waits until they have typed. Without this the modal renders with a red
  // "Layout name is required" already showing.
  const [nameTouched, setNameTouched] = useState(false);

  const clientNameError = validateLayoutName({
    name,
    isShared: targetIsShared,
    layouts,
    ...(layoutToEdit?._id !== undefined && { excludeId: layoutToEdit._id }),
  });
  // The server's verdict wins. Only EMPTY is deferred, and only until the first edit — a clash or a
  // reserved name is worth showing immediately, since the user typed something concrete.
  const nameError =
    serverNameError ?? (clientNameError === LAYOUT_NAME_ERROR.EMPTY && !nameTouched ? null : clientNameError);
  const nameErrorText = nameError ? t(LAYOUT_NAME_ERROR_TEXT[nameError]) : "";

  // In edit ("Configure") mode, only enable Save once something actually changed — the name or
  // the ordered module list — so we don't fire an update API call with no modifications.
  const addedModuleIds = added.map((item) => item.value);
  const hasChanges =
    name.trim() !== (layoutToEdit?.name ?? "").trim() ||
    addedModuleIds.length !== initialAdded.length ||
    addedModuleIds.some((id, index) => id !== initialAdded[index]?.value);
  const canSave = clientNameError === null && !serverNameError && added.length > 0 && (!isEdit || hasChanges);

  const renderModuleRow = (item: ModuleItem) => (
    <span className="flex items-center gap-2">
      {item.svg ? <IconComponent name={item.svg} width="1rem" height="1rem" /> : null}
      {item.text}
    </span>
  );

  /**
   * Shows a refused save. A name clash lands INLINE on the field and keeps the modal open, so the
   * user can correct it in place; anything else is a toast. A missing CREATE_SHARED_LAYOUTS
   * permission and an immutable system layout each say something specific, and reporting them all as
   * "Something went wrong" left the user with no idea why the save failed — so the two structural
   * refusals are worded here from their string code, and everything else is translated from its
   * numeric code by `resolveErrorContent`. The generic copy is only for a body carrying neither.
   */
  const reportFailure = (data: { errorCode?: number | string | undefined }) => {
    if (data.errorCode === LAYOUT_ERROR_CODE.NAME_ALREADY_EXISTS) {
      setServerNameError(LAYOUT_NAME_ERROR.DUPLICATE);
      return;
    }
    const { errorCode } = data;
    const content =
      errorCode === LAYOUT_ERROR_CODE.SHARED_PERMISSION_DENIED
        ? t("You do not have permission to perform this action.")
        : errorCode === LAYOUT_ERROR_CODE.SYSTEM_LAYOUT_IMMUTABLE
          ? t("System layouts cannot be renamed, configured or deleted.")
          : typeof errorCode === "number"
            ? resolveErrorContent({ status: false, errorCode })
            : t("Something went wrong");
    dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
  };

  const handleSave = async () => {
    if (!canSave || isLayoutSaving) return;
    setIsLayoutSaving(true);
    setServerNameError(null);
    try {
      const modules = added.map((item) => item.value);
      if (layoutToEdit) {
        const response = await axiosWrapper.post<UpdateViewResponse>(`${getBackendUrl()}/layoutView/updateView`, {
          id: layoutToEdit._id,
          updateData: {
            name: name.trim(),
            data: { ...(layoutToEdit.data ?? {}), modules },
          },
        });
        if (response.data.status) {
          const updated: LayoutData = {
            ...layoutToEdit,
            name: name.trim(),
            data: { ...(layoutToEdit.data ?? {}), modules },
          };
          // Success only — no affected-user counts, even for a shared layout. Other users pick the
          // change up on their next load, never mid-session.
          dispatch(showToastNotification({ content: t("Layout updated successfully"), type: TOAST_TYPE_SUCCESS }));
          onSaved(updated, true);
          onClose();
        } else {
          reportFailure(response.data);
        }
        return;
      }

      // No projectId: sidebar layouts are portal-level. Sending one would plant a project-scoped copy
      // that then vanishes from the portal-level listing.
      const response = await axiosWrapper.post<CreateViewResponse>(`${getBackendUrl()}/layoutView/createView`, {
        createData: {
          resourceType: PROJECT_DASHBOARD_RESOURCE_TYPE,
          name: name.trim(),
          isShared,
          data: { modules },
        },
      });
      if (response.data.status) {
        const created = response.data.result;
        dispatch(showToastNotification({ content: t("Layout created successfully"), type: TOAST_TYPE_SUCCESS }));
        // The caller opens the apply-scope dialog next: the layout is created first, scope second.
        onSaved(created, false);
        onClose();
      } else {
        reportFailure(response.data);
      }
    } catch {
      // Network / unexpected failure — surface it instead of leaving the modal frozen with no feedback.
      dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
    } finally {
      setIsLayoutSaving(false);
    }
  };

  const modalContent = (
    <div className="flex h-full flex-col">
      <div className="my-4 flex items-start gap-4">
        <Text className="whitespace-nowrap pt-2 text-sm font-normal">{t("Layout Name")}</Text>
        <div className="flex-1">
          <TextFieldComponent
            placeholder={t("Enter Name")}
            value={name}
            error={Boolean(nameErrorText)}
            helperText={nameErrorText}
            onChange={(e) => {
              setName(e.target.value);
              setNameTouched(true);
              // The server's verdict is about the name it was given; editing invalidates it.
              setServerNameError(null);
            }}
            data-testid="layoutNameInput"
          />
        </div>
      </div>
      <DragListComponent
        data1={available}
        data2={added}
        setData1={setAvailable}
        setData2={setAdded}
        fields={{ text: "text", value: "value" }}
        noItemsText={t("No Modules Added")}
        itemTemplate={renderModuleRow}
        showHint={false}
        showButtons
        showSearch
        allowMultiSelect
        fillHeight
      />
    </div>
  );

  const footerContent = (
    <div className="flex gap-3">
      <ButtonComponent
        buttonType="gradientButton"
        disabled={!canSave}
        loading={isLayoutSaving}
        // Fire-and-forget: handleSave owns the saving flag, reports both outcomes and closes on success.
        onClick={() => void handleSave()}
        data-testid="createLayoutSubmit"
      >
        {t("Save Changes")}
      </ButtonComponent>
      <ButtonComponent buttonType="borderButton" onClick={onClose} data-testid="createLayoutCancel">
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      isVisible
      headerTitle={isEdit ? t("Configure Layout") : t("Create Layout")}
      modelContent={modalContent}
      footerContent={footerContent}
      onCancel={onClose}
      height="75%"
      width="56.25rem"
    />
  );
}

export default ConfigureLayoutModal;
